const normalizeVenueTypeValue = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim().toLowerCase();
};

const extractVenueTypeLabel = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number') return '';
    if (typeof value === 'object') {
        const label =
            value.name ??
            value.label ??
            value.description ??
            value.display ??
            value.title;
        if (label === null || label === undefined) return '';
        return String(label).trim();
    }
    return '';
};

const getVenueParentLabel = (venue) => {
    if (!venue || typeof venue !== 'object') return '';
    const candidates = [
        venue.parent_category,
        venue.parentCategory,
        venue.parent_category_name,
        venue.parentCategoryName,
        venue.parent_name,
        venue.parentName,
        venue.category,
        venue.category_name,
        venue.categoryName,
    ];
    for (const candidate of candidates) {
        const label = extractVenueTypeLabel(candidate);
        if (label) return label;
    }
    return '';
};

const buildDisambiguatedLabel = (baseLabel, parentLabel) => {
    const base = String(baseLabel ?? '').trim();
    if (!base) return '';
    const parent = String(parentLabel ?? '').trim();
    if (!parent) return base;
    const normalizedBase = normalizeVenueTypeValue(base);
    const normalizedParent = normalizeVenueTypeValue(parent);
    if (!normalizedParent || normalizedParent === normalizedBase) return base;
    if (normalizedBase.startsWith(normalizedParent)) return base;
    return `${parent} ${base}`;
};

const buildVenueTypeCatalogIndex = (venueTypesCatalog) => {
    const map = new Map();
    (venueTypesCatalog || []).forEach((venueType) => {
        if (!venueType) return;
        const idValue =
            venueType.id ??
            venueType.value ??
            venueType.code ??
            venueType.name;
        if (idValue == null) return;
        map.set(String(idValue), venueType);
    });
    return map;
};

const buildVenueTypeEntries = ({
    screens = [],
    venueTypesCatalog = [],
    includeCatalogEntries = false,
    availableVenueTypeIds = [],
} = {}) => {
    const venueTypeById = buildVenueTypeCatalogIndex(venueTypesCatalog);
    const allowedCatalogIds = new Set(
        (Array.isArray(availableVenueTypeIds) ? availableVenueTypeIds : [])
            .map((value) => normalizeVenueTypeValue(value))
            .filter(Boolean)
    );
    const map = new Map();
    const aliasToKey = new Map();

    const resolveParentLabel = (candidate, primaryValue) => {
        const direct = getVenueParentLabel(candidate);
        if (direct) return direct;

        const candidateId =
            typeof candidate === 'object'
                ? candidate.id ??
                  candidate.venue_type_id ??
                  candidate.value ??
                  candidate.code
                : primaryValue;

        if (candidateId === null || candidateId === undefined) return '';

        const lookup = venueTypeById.get(String(candidateId));
        if (lookup?.parentCategoryName) {
            return String(lookup.parentCategoryName).trim();
        }
        const parentId =
            lookup?.parentCategory ??
            lookup?.parent_category ??
            lookup?.raw?.parent_category;
        if (parentId === null || parentId === undefined) return '';

        const parentLookup = venueTypeById.get(String(parentId));
        const parentName =
            parentLookup?.name ??
            parentLookup?.label ??
            parentLookup?.description;
        return parentName ? String(parentName).trim() : '';
    };

    const ensureEntry = (normalizedKey, idValue, labelValue, parentLabel) => {
        if (!normalizedKey) return null;

        const canonicalKey = aliasToKey.get(normalizedKey) ?? normalizedKey;
        let entry = map.get(canonicalKey);
        if (!entry) {
            const resolvedLabel = labelValue ?? idValue ?? normalizedKey;
            entry = {
                id: idValue ?? resolvedLabel ?? normalizedKey,
                label: resolvedLabel,
                baseLabel: resolvedLabel,
                normalizedKey: canonicalKey,
                aliases: new Set(),
                count: 0,
                parentLabel: parentLabel || '',
            };
            map.set(canonicalKey, entry);
            aliasToKey.set(canonicalKey, canonicalKey);
        } else {
            if (idValue && !entry.id) entry.id = idValue;
            if (labelValue && !entry.label) entry.label = labelValue;
            if (labelValue && !entry.baseLabel) entry.baseLabel = labelValue;
            if (!entry.parentLabel && parentLabel)
                entry.parentLabel = parentLabel;
        }
        return entry;
    };

    const registerAlias = (entry, value) => {
        const normalizedAlias = normalizeVenueTypeValue(value);
        if (!normalizedAlias) return;
        entry.aliases.add(normalizedAlias);

        if (!aliasToKey.has(normalizedAlias)) {
            aliasToKey.set(normalizedAlias, entry.normalizedKey);
        }
    };

    const collectVenueAliases = (venue, entry) => {
        if (!venue) return;
        if (Array.isArray(venue)) {
            venue.forEach((item) => collectVenueAliases(item, entry));
            return;
        }

        if (typeof venue === 'object') {
            registerAlias(entry, venue.id);
            registerAlias(entry, venue.venue_type_id);
            registerAlias(entry, venue.value);
            registerAlias(entry, venue.code);
            registerAlias(entry, venue.name);
            registerAlias(entry, venue.label);
            registerAlias(entry, venue.description);
            registerAlias(entry, venue.display);
        } else {
            registerAlias(entry, venue);
        }
    };

    const useCatalogAsSource =
        includeCatalogEntries && allowedCatalogIds.size > 0;
    const baseScreens =
        useCatalogAsSource || !Array.isArray(screens) ? [] : screens;

    baseScreens.forEach((screen) => {
        const rawValues = [
            screen?.venue_type,
            screen?.venueType,
            screen?.venue_type_id,
            screen?.screen_type,
        ];

        // Track which canonical keys were already counted for this screen so that
        // multiple fields pointing to the same entry (e.g. venue_type + screen_type)
        // only increment the count once.
        const countedKeysForScreen = new Set();

        rawValues.forEach((value) => {
            if (!value) return;

            const candidates = Array.isArray(value) ? value : [value];
            candidates.forEach((candidate) => {
                let primaryValue = candidate;
                if (typeof candidate === 'object') {
                    primaryValue =
                        candidate.id ??
                        candidate.venue_type_id ??
                        candidate.value ??
                        candidate.code ??
                        candidate.name ??
                        candidate.label;
                }
                const normalizedKey = normalizeVenueTypeValue(primaryValue);
                if (!normalizedKey) return;

                const baseLabel =
                    extractVenueTypeLabel(candidate) ||
                    String(primaryValue || normalizedKey);
                const parentLabel = resolveParentLabel(candidate, primaryValue);

                const entry = ensureEntry(
                    normalizedKey,
                    String(primaryValue || normalizedKey),
                    baseLabel,
                    parentLabel
                );
                if (!entry) return;

                collectVenueAliases(candidate, entry);
                if (!countedKeysForScreen.has(entry.normalizedKey)) {
                    countedKeysForScreen.add(entry.normalizedKey);
                    entry.count += 1;
                }
            });
        });
    });

    if (includeCatalogEntries && Array.isArray(venueTypesCatalog)) {
        venueTypesCatalog.forEach((venueType) => {
            if (!venueType) return;
            const primaryValue =
                venueType.id ??
                venueType.venue_type_id ??
                venueType.value ??
                venueType.code ??
                venueType.name;
            if (primaryValue === null || primaryValue === undefined) return;

            const normalizedKey = normalizeVenueTypeValue(primaryValue);
            if (!normalizedKey) return;
            if (
                allowedCatalogIds.size > 0 &&
                !allowedCatalogIds.has(normalizedKey)
            ) {
                return;
            }

            const baseLabel =
                extractVenueTypeLabel(venueType) || String(primaryValue);
            const parentLabel = resolveParentLabel(venueType, primaryValue);
            const entry = ensureEntry(
                normalizedKey,
                String(primaryValue),
                baseLabel,
                parentLabel
            );
            if (!entry) return;

            registerAlias(entry, primaryValue);
            collectVenueAliases(venueType, entry);
        });
    }

    if (includeCatalogEntries && allowedCatalogIds.size > 0) {
        allowedCatalogIds.forEach((normalizedId) => {
            if (map.has(normalizedId)) return;
            ensureEntry(normalizedId, normalizedId, normalizedId, '');
        });
    }

    const entries = Array.from(map.values());
    const labelCounts = new Map();

    entries.forEach((entry) => {
        const baseLabel = entry.baseLabel ?? entry.label ?? entry.id;
        const key = normalizeVenueTypeValue(baseLabel);
        if (!key) return;
        labelCounts.set(key, (labelCounts.get(key) || 0) + 1);
    });

    return entries
        .map((entry) => {
            const baseLabel = entry.baseLabel ?? entry.label ?? entry.id;
            const baseKey = normalizeVenueTypeValue(baseLabel);
            const shouldDisambiguate =
                baseKey && (labelCounts.get(baseKey) || 0) > 1;
            const label = shouldDisambiguate
                ? buildDisambiguatedLabel(baseLabel, entry.parentLabel) ||
                  baseLabel
                : baseLabel;

            return {
                id: entry.id,
                label,
                normalizedKey: entry.normalizedKey,
                aliases: Array.from(entry.aliases),
                count: entry.count,
            };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
};

const buildVenueTypeLabelLookup = (entries = []) => {
    const map = new Map();
    (entries || []).forEach((entry) => {
        if (!entry) return;
        const label = entry.label ?? entry.baseLabel ?? entry.id ?? '';
        if (!label) return;
        if (entry.normalizedKey) map.set(entry.normalizedKey, label);
        if (Array.isArray(entry.aliases)) {
            entry.aliases.forEach((alias) => {
                if (!alias) return;
                map.set(alias, label);
            });
        }
    });
    return map;
};

export {
    buildVenueTypeEntries,
    buildVenueTypeLabelLookup,
    extractVenueTypeLabel,
    normalizeVenueTypeValue,
};
